import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';

export type InternalUser = {
  id: string;
  displayName: string;
  email: string;
  firstName?: string;
  lastName?: string;
};

export type InternalConversation = {
  id: string;
  kind: 'DIRECT' | 'GROUP';
  contextType: 'GENERAL' | 'JOB';
  contextId: string | null;
  title: string;
  members: Array<{
    id: string;
    user: InternalUser;
    source: 'MANUAL' | 'JOB_ASSIGNMENT';
    isOwner: boolean;
    joinedAt: string;
  }>;
  unreadCount: number;
  lastMessageAt: string;
  lastMessage: {
    id: string;
    body: string;
    createdAt: string;
    senderUserId: string;
    attachmentCount: number;
  } | null;
  canManageMembers: boolean;
};

export type InternalMessage = {
  id: string;
  conversationId: string;
  body: string;
  replyToMessageId: string | null;
  createdAt: string;
  sender: InternalUser;
  attachments: InternalAttachment[];
};

export type InternalAttachment = {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
  viewUrl: string;
};

export type InternalConversationAttachment = InternalAttachment & {
  sender: InternalUser;
  message: {
    id: string;
    body: string;
    createdAt: string;
  } | null;
};

export const internalCommunicationKeys = {
  all: ['internal-communications'] as const,
  conversations: () => [...internalCommunicationKeys.all, 'conversations'] as const,
  conversation: (id: string) => [...internalCommunicationKeys.all, 'conversation', id] as const,
  messages: (id: string) => [...internalCommunicationKeys.all, 'messages', id] as const,
  attachments: (id: string) => [...internalCommunicationKeys.all, 'attachments', id] as const,
  recipients: (search = '') => [...internalCommunicationKeys.all, 'recipients', search] as const,
  unread: () => [...internalCommunicationKeys.all, 'unread'] as const,
};

export async function listInternalConversations(
  scope: 'mine' | 'all' = 'mine',
  options?: { search?: string; limit?: number; contextType?: 'GENERAL' | 'JOB' },
) {
  const params = new URLSearchParams({ scope });
  const search = options?.search?.trim();
  if (search) params.set('search', search);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.contextType) params.set('contextType', options.contextType);
  return apiGet<{ items: InternalConversation[] }>(
    operationsClient,
    `/internal-messages/conversations?${params.toString()}`,
  );
}

export async function listInternalRecipients(options?: { search?: string; limit?: number }) {
  const params = new URLSearchParams({
    limit: String(options?.limit ?? 100),
  });
  const search = options?.search?.trim();
  if (search) params.set('search', search);
  return apiGet<{ items: InternalUser[] }>(
    operationsClient,
    `/internal-messages/recipients?${params.toString()}`,
  );
}

export async function getInternalConversation(conversationId: string) {
  return apiGet<InternalConversation>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}`,
  );
}

export async function getInternalUnreadCount(options?: { contextType?: 'GENERAL' | 'JOB' }) {
  const params = new URLSearchParams();
  if (options?.contextType) params.set('contextType', options.contextType);
  const query = params.toString();
  return apiGet<{ count: number }>(
    operationsClient,
    `/internal-messages/unread-count${query ? `?${query}` : ''}`,
  );
}

export async function createInternalConversation(input: {
  participantUserIds: string[];
  title?: string;
}) {
  return apiPost<InternalConversation, typeof input>(
    operationsClient,
    '/internal-messages/conversations',
    input,
  );
}

export async function openJobConversation(jobId: string) {
  return apiPost<InternalConversation, Record<string, never>>(
    operationsClient,
    `/internal-messages/jobs/${jobId}`,
    {},
  );
}

export async function listInternalMessages(
  conversationId: string,
  options?: { cursor?: string; limit?: number },
) {
  const params = new URLSearchParams({
    limit: String(options?.limit ?? 100),
  });
  if (options?.cursor) params.set('cursor', options.cursor);
  return apiGet<{ items: InternalMessage[]; nextCursor: string | null }>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}/messages?${params.toString()}`,
  );
}

export async function sendInternalMessage(
  conversationId: string,
  input: {
    clientMessageId: string;
    body: string;
    attachmentIds?: string[];
  },
) {
  return apiPost<InternalMessage, typeof input>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}/messages`,
    input,
  );
}

export async function createInternalAttachmentUpload(
  conversationId: string,
  file: File,
  clientAttachmentId: string,
) {
  const mimeType = file.type.toLowerCase();
  if (!mimeType) throw new Error(`${file.name} has an unknown file type`);
  const result = await apiPost<
    {
      uploadUrl: string | null;
      attachment: Pick<InternalAttachment, 'id' | 'fileName' | 'fileSize' | 'mimeType'>;
    },
    {
      clientAttachmentId: string;
      fileName: string;
      fileSize: number;
      mimeType: string;
    }
  >(operationsClient, `/internal-messages/conversations/${conversationId}/attachments/upload-url`, {
    clientAttachmentId,
    fileName: file.name,
    fileSize: file.size,
    mimeType,
  });
  if (result.uploadUrl) {
    const upload = await fetch(result.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType, 'If-None-Match': '*' },
      body: file,
    });
    // A conditional retry receives 412 when the first PUT already landed.
    if (!upload.ok && upload.status !== 412) throw new Error(`Could not upload ${file.name}`);
  }
  return result.attachment;
}

export async function listInternalAttachments(
  conversationId: string,
  options?: { cursor?: string; limit?: number },
) {
  const params = new URLSearchParams({
    limit: String(options?.limit ?? 50),
  });
  if (options?.cursor) params.set('cursor', options.cursor);
  return apiGet<{
    items: InternalConversationAttachment[];
    nextCursor: string | null;
  }>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}/attachments?${params.toString()}`,
  );
}

export async function markInternalConversationRead(conversationId: string) {
  return apiPost<{ ok: true }, Record<string, never>>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}/read`,
    {},
  );
}

export async function renameInternalConversation(conversationId: string, title: string) {
  return apiPatch<InternalConversation, { title: string }>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}`,
    { title },
  );
}

export async function addInternalConversationMembers(conversationId: string, userIds: string[]) {
  return apiPost<InternalConversation, { userIds: string[] }>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}/members`,
    { userIds },
  );
}

export async function removeInternalConversationMember(conversationId: string, userId: string) {
  return apiDelete<{ ok: true }>(
    operationsClient,
    `/internal-messages/conversations/${conversationId}/members/${userId}`,
  );
}
